import { memo, useCallback, useMemo } from 'react';
import { HighwayArea } from '@thive/fddm-types';

import { SelectInput } from '@common/components/inputs';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { getCommonOption } from '@/modules/common/helpers/commonValue';
import { useTranslatedOptions } from '@/modules/common/hooks';

import { DisabledReasonTooltip } from '../common/DisabledReasonTooltip';

const OPTIONS: { labelKey: string; value: HighwayArea['connectUturns'] }[] = [
  { labelKey: 'interface:properties.highway.connection_options.no', value: 'NO_UTURN_MAPPING' },
  {
    labelKey: 'interface:properties.highway.connection_options.curved',
    value: 'MAP_UTURNS_WITHOUT_PIVOTS',
  },
  {
    labelKey: 'interface:properties.highway.connection_options.pivot',
    value: 'MAP_UTURNS_WITH_PIVOTS',
  },
];

type Props = {
  selectedHighwayAreas: HighwayArea[];
};

const isTwoWay = (highway: HighwayArea): boolean =>
  highway.highwayPoiSpecs[0].roadType !== 'ONE_WAY';

function HighwayConnectUturnsComponent({ selectedHighwayAreas }: Props) {
  const optionsOptions = useTranslatedOptions(OPTIONS);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const twoWayHighways = useMemo(
    () => selectedHighwayAreas.filter(isTwoWay),
    [selectedHighwayAreas],
  );

  // U-turns only exist on two-way highways: like the other partial selections, only editable when
  // all selected highways are two-way
  const isPartialSelection = twoWayHighways.length !== selectedHighwayAreas.length;

  const value = useMemo(() => {
    const highways = twoWayHighways.length > 0 ? twoWayHighways : selectedHighwayAreas;
    return getCommonOption(highways.map(({ connectUturns }) => connectUturns));
  }, [selectedHighwayAreas, twoWayHighways]);

  const onChange = useCallback(
    (newValue: HighwayArea['connectUturns']) => {
      if (isPartialSelection) return;

      const updatedAreas = createEmptyAreas();
      twoWayHighways.forEach((highway) => {
        pushAreaToItsCollection(updatedAreas, { ...highway, connectUturns: newValue });
      });

      fddmSvc.setPreference('highwayArea.connectUturns', newValue);
      fddmSvc.updateAreas(updatedAreas);
    },
    [isPartialSelection, twoWayHighways, fddmSvc],
  );

  return (
    <DisabledReasonTooltip
      reasonKey={
        isPartialSelection
          ? 'interface:properties.highway.partial_selection.only_two_way'
          : undefined
      }
    >
      <SelectInput
        options={optionsOptions}
        onChange={onChange}
        value={value}
        disabled={isPartialSelection}
        showMixed
      />
    </DisabledReasonTooltip>
  );
}

export const HighwayConnectUturns = memo(HighwayConnectUturnsComponent);
